import React, { useEffect, useMemo, useState } from "react";
import DocViewer, { DocViewerRenderers } from "react-doc-viewer";
import { FlashMessage, Modal, Spinner, ConfirmDialog } from "../../../components/common";
import resumeIcon from "../../../assets/svg/resume-icon.svg";
import experienceIcon from "../../../assets/svg/experience-view-icon.svg";
import projectIcon from "../../../assets/svg/projects-view-icon.svg";
import educationIcon from "../../../assets/svg/education-view-icon.svg";
import accomplishmentIcon from "../../../assets/svg/accomplishments-view-icon.svg";
import {
  IconButton,
  Button,
  Menu,
  MenuItem,
} from "@material-ui/core/";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import VideoModal from "./profileVideoModal";
import { get, isEmpty } from "lodash";
import copyToClipboard from "copy-to-clipboard";

import {
  getCandidateProfileRequest,
  candidateProfileReset,
  sendInvitationRequest,
  resetSendInvitationFlag,
} from "../../../reducers/SearchAndConnect/searchAndConnect.reducer";
import appRoutes from "../../../routes/app.routes";
import Avatar from "../../../components/common/AvtarImage";
import {
  formatDate,
  getGenderLabelSuffix,
  getRemainingResendHours,
  htmlToPlainText,
  openUrlInNewTab,
  storeNewMessageInfo,
} from "../../../utils/helper";
import { canReceiveMessages } from "../../../utils/messaging.utils";
import { tabChangeRequest } from "../../../reducers/company/tabChange.reducer";
import { rootReducersState } from "../../../reducers";
import httpRequest from "../../../utils/httpRequest";
import InvitationModal from "./InvitationModal";
import { ReducerFlag } from "../../../types/reducer.types";
import { connectionRequestStatus, RESEND_CONNECTION_REQUEST_TIME_LIMIT } from "../../../utils/appConstants";
import * as searchAndConnectServices from "../../../services/searchAndConnect.services";
import LinkSVG from "../../../assets/svg/LinkSVG";
import DotsIcon from "../../../components/ProfileViewNew/icons/dots-red.svg";

const _ = { get, isEmpty };

const splitQuickViewServiceNames = (serviceName: string | undefined) =>
  (serviceName || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);


/** Matches ProfileViewNew WorkExperience `getDisplayedJobTitle` */
function getQuickViewDisplayedJobTitle(item: any): string {
  const custom = item?.job_title_custom;
  if (custom != null && typeof custom === "object" && !Array.isArray(custom)) {
    const fromObj = String(
      _.get(custom, "label", "") || _.get(custom, "value", "") || ""
    ).trim();
    if (fromObj) return fromObj;
  }
  if (typeof custom === "string" && custom.trim()) {
    return custom.trim();
  }
  const jobTitle = item?.jobTitle;
  if (jobTitle != null && typeof jobTitle === "object") {
    const label = String(_.get(jobTitle, "label", "") || "").trim();
    if (label) return label;
  }
  if (typeof jobTitle === "string" && jobTitle.trim()) {
    return jobTitle.trim();
  }
  return "";
}

function formatQuickViewExperienceHeadline(item: any): string {
  const title = getQuickViewDisplayedJobTitle(item);
  const company = String(item?.company_name || "").trim();
  return [title, company].filter(Boolean).join(" - ");
}

function formatQuickViewCertificateDetail(cert: any): string {
  const parts: string[] = [];
  const org = String(cert?.organization_name || "").trim();
  if (org) {
    parts.push(org);
  }
  if (cert?.issue_date) {
    parts.push(`Issued ${formatDate(cert.issue_date)}`);
  }
  if (cert?.expiry_date) {
    parts.push(`Expires ${formatDate(cert.expiry_date)}`);
  }
  const url = String(cert?.cert_url || "").trim();
  if (url) {
    parts.push(url);
  }
  return parts.join(" · ");
}

function renderQuickViewServiceEntry(
  service: Record<string, unknown>,
  index: number
) {
  const names = splitQuickViewServiceNames(
    service?.service_name as string | undefined
  );

  return (
    <div
      className="services-quick-view-entry"
      key={
        (service?.updated_at as string) ||
        (service?.created_at as string) ||
        String(index)
      }
    >
      {names.length > 0 ? (
        <div className="skills-list">
          {names.map((name, nameIdx) => (
            <div
              className="skill-item"
              key={`${index}-${nameIdx}-${name}`}
            >
              {name}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const CandidatesModal = (props: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    search: { profile, profileLoading, sendInviteFlag, sendInviteData },
  } = useSelector(({ search }: any) => search);
  const sessionReducer = useSelector(
    ({ session }: rootReducersState) => session.currentUser
  );
  const sessionUserEmail = _.get(sessionReducer, "email", "");
  const profileUserEmail = _.get(profile, "email", "");
  const isOwnProfile =
    !!sessionUserEmail &&
    !!profileUserEmail &&
    sessionUserEmail === profileUserEmail;
  const [candidateVideoUrl, setCandidateVideoUrl] = useState("");
  const [videoModalStatus, setVideoOpen] = useState(false);
  const [showPersonalValues, setShowPersonalValues] = useState(false);
  const [invitationModalStatus, setInvitationOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [connectionId, setConnectionId] = useState<any>(null);
  const [cancelSentRequestShow, setCancelSentRequestShow] = useState(false);
  const [resumeViewModal, setResumeViewModal] = useState(false);
  const [resumePreviewPath, setResumePreviewPath] = useState("");
  const [publicResumeMenuAnchor, setPublicResumeMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [publicMenuResume, setPublicMenuResume] = useState<{
    id?: number;
    resume_file: string;
    original_file?: string;
    created_at?: string;
  } | null>(null);
  const [loadingPublicResumePreview, setLoadingPublicResumePreview] =
    useState(false);
  const [quickViewProfileMenuAnchor, setQuickViewProfileMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [shareProfileModalOpen, setShareProfileModalOpen] = useState(false);
  const [msgMenuAnchor, setMsgMenuAnchor] = useState<null | HTMLElement>(null);

  const quickViewResumes = useMemo(() => {
    const resumes = _.get(profile, "resumes", []);
    if (Array.isArray(resumes) && resumes.length > 0) {
      return resumes;
    }
  }, [profile]);

  const connectRequestStatusText = useMemo(() => {
    const connectRequestStatusObj = connectionRequestStatus.find(
      (row) => row.status_id === (connectionStatus || 0)
    );
    return _.get(connectRequestStatusObj, "status_text", "");
  }, [connectionStatus]);

  const connectAccepted = connectRequestStatusText === "accepted";
  const showMessageButton =
    ((sessionReducer?.companies?.length ?? 0) > 0 || connectAccepted) &&
    canReceiveMessages(profile);

  const candidatePublicProfileUrl = useMemo(
    () =>
      appRoutes.candidatePublicProfile.generateFullPath(
        _.get(profile, "slug", "") || ""
      ),
    [profile]
  );

  const quickViewAccomplishments = useMemo(() => {
    const list = _.get(profile, "accomplishments", []);
    return Array.isArray(list) ? list : [];
  }, [profile]);

  const quickViewCertificates = useMemo(() => {
    const list =
      _.get(profile, "candidateCertificates", []) ||
      _.get(profile, "candidate_certificates", []);
    return Array.isArray(list) ? list : [];
  }, [profile]);

  const jobTitle = _.get(profile?.profile, "jobTitle.title", "");
  const workStatus = _.get(profile?.profile, "work_status", "");
  const jobTitleCustom = _.get(profile?.profile, "job_title_custom", "");

  const videoShowModal = (url: any) => {
    setCandidateVideoUrl(url);
    setVideoOpen(true);
  };

  const videoModalHandleClose = () => {
    setVideoOpen(false);
  };

  const candidateModalHandleClose = () => {
    setShareProfileModalOpen(false);
    setQuickViewProfileMenuAnchor(null);
    setMsgMenuAnchor(null);
    dispatch(candidateProfileReset({}));
    props.handleOnClose();
  };

  const handleQuickViewMessageClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    const currentUserId = _.get(sessionReducer, "id", "");
    if (!currentUserId) {
      FlashMessage("Please log in to send a message", "error");
      return;
    }
    if (isOwnProfile) return;
    setMsgMenuAnchor(event.currentTarget);
  };

  const handleQuickViewMessageMenuClose = () => {
    setMsgMenuAnchor(null);
  };

  const handleQuickViewRedirectToMessages = (
    isCompany = false,
    companyRow?: { slug?: string; company_name?: string }
  ) => {
    storeNewMessageInfo({
      slug: _.get(profile, "slug", ""),
      uuid: _.get(profile, "id", 0),
      first_name: _.get(profile, "first_name", ""),
      isCompany: isCompany ? 1 : null,
      last_name: _.get(profile, "last_name", ""),
      profile_image: _.get(profile, "profile_image", ""),
    });

    setMsgMenuAnchor(null);
    dispatch(candidateProfileReset({}));
    props.handleOnClose();

    if (isCompany && companyRow?.slug) {
      dispatch(tabChangeRequest({ tab: "messages" }));
      navigate(appRoutes.companyMessages.generatePath(companyRow.slug));
    } else {
      navigate(appRoutes.candidateMessages.altPath);
    }
  };

  const copyCandidatePublicProfileUrl = () => {
    if (!candidatePublicProfileUrl) {
      FlashMessage("Profile link unavailable", "error");
      return;
    }
    copyToClipboard(candidatePublicProfileUrl);
    FlashMessage("Copied link");
  };

  const openCandidatePublicProfileInNewTab = () => {
    setQuickViewProfileMenuAnchor(null);
    if (!candidatePublicProfileUrl) {
      FlashMessage("Profile link unavailable", "error");
      return;
    }
    openUrlInNewTab(candidatePublicProfileUrl);
  };

  const openShareProfileModal = () => {
    setQuickViewProfileMenuAnchor(null);
    setShareProfileModalOpen(true);
  };

  const renderPersonalValues = (personalValues: string | null | undefined) => {
    // Validation: Check if personalValues exists and is not empty
    if (!personalValues || typeof personalValues !== 'string' || personalValues.trim().length === 0) {
      return null;
    }

    const trimmedText = htmlToPlainText(personalValues);
    if (!trimmedText) return null;
    const shouldTruncate = trimmedText.length > 100;

    // If text is 100 characters or less, show full text without Read More
    if (!shouldTruncate) {
      return trimmedText;
    }

    // If text is more than 100 characters, show truncated text with Read More/Show Less
    return (
      <>
        {showPersonalValues ? trimmedText : trimmedText.substring(0, 100) + "..."}
        {!showPersonalValues ? (
          <span className="read-more" onClick={() => setShowPersonalValues(true)}>Read More</span>
        ) : (
          <span className="read-less" onClick={() => setShowPersonalValues(false)}>Show Less</span>
        )}
      </>
    );
  };

  const invitationShowModal = () => {
    setInvitationOpen(true);

  };

  const invitationModalHandleClose = () => {
    setInvitationOpen(false);
  };

  const handleResumeDownload = (resume: { resume_file?: string }) => {
    setPublicResumeMenuAnchor(null);
    setPublicMenuResume(null);
    if (resume.resume_file) {
      openUrlInNewTab(resume.resume_file);
    } else {
      FlashMessage("No file available", "error");
    }
  };

  const handleShowResume = async (resume: {
    id?: number;
    resume_file: string;
  }) => {
    setPublicResumeMenuAnchor(null);
    setPublicMenuResume(null);
    try {
      const slug = _.get(profile, "slug", "");
      if (!slug) {
        FlashMessage("Resume not found", "error");
        return;
      }
      setLoadingPublicResumePreview(true);
      const base = `candidates/profile/public/resume/${slug}/preview`;
      const previewUrl =
        resume.id != null && resume.id > 0
          ? `${base}?resume_id=${resume.id}`
          : base;
      const { data } = await httpRequest().post(previewUrl);
      const path = data.path;
      if (path) {
        setResumePreviewPath(path);
        setResumeViewModal(true);
      } else {
        FlashMessage("Resume not found", "error");
      }
    } catch {
      FlashMessage("Resume not found", "error");
    } finally {
      setLoadingPublicResumePreview(false);
    }
  };

  const handleOnInviteSent = ({ note }) => {
    dispatch(
      sendInvitationRequest({ note: note || "", id: _.get(profile, "id") })
    );
  };

  const handleCancelSentRequest = async () => {
    try {
      const cancelledStatus =
        connectionRequestStatus.find(
          (row) => row.status_text === "cancelled_24_hours"
        )?.status_id || 0;

      await searchAndConnectServices.updateCandidateConnection({
        id: connectionId,
        type: "cancelled",
        reject_reason: "",
      });

      // Update the local state
      setConnectionStatus(cancelledStatus);
      FlashMessage("Connection request withdrawn successfully!");
    } catch (error) {
      const errMessage = _.get(error, "response.data.message", "Something went wrong");
      FlashMessage(errMessage, "error");
    }

    setCancelSentRequestShow(false);
  };

  const renderConnectButton = () => {
    const connectRequestStatusId = connectionStatus || 0;
    const connectRequestStatusObj = connectionRequestStatus.find(
      (row) => row.status_id === connectRequestStatusId
    );
    const connectRequestStatusText = _.get(
      connectRequestStatusObj,
      "status_text",
      ""
    );

    switch (connectRequestStatusText) {
      case "accepted":
        return (
          <Button variant="contained" color="primary" disabled>
            Connected
          </Button>
        );
      case "rejected":
        return (
          <Button variant="contained" color="primary" disabled>
            Rejected
          </Button>
        );
      case "pending":
        return (
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setCancelSentRequestShow(true)}
          >
            Withdraw
          </Button>
        );
      case "cancelled_24_hours": {
        const remainingHours = getRemainingResendHours(
          _.get(profile, "connect.cancelled_at", "") ||
          _.get(profile, "follow.cancelled_at", "")
        );
        return (
          <Button variant="contained" color="primary" disabled style={{ fontSize: "var(--fs-body2)" }}>
            Wait {remainingHours} hours
          </Button>
        );
      }
      default:
        return (
          <Button variant="contained" color="primary" onClick={invitationShowModal}>
            Connect
          </Button>
        );
    }
  };

  useEffect(() => {
    if (props.payload.slug && props?.status) {
      dispatch(getCandidateProfileRequest({ slug: props.payload.slug }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.status]);

  // Initialize connection status when profile loads
  useEffect(() => {
    if (profile && !_.isEmpty(profile)) {
      // Check both connect and follow objects (API returns different keys)
      const existingConnectionStatus = _.get(profile, "connect.status", null) || _.get(profile, "follow.status", null);
      const existingConnectionId = _.get(profile, "connect.connect_id", null) || _.get(profile, "follow.id", null);
      setConnectionStatus(existingConnectionStatus);
      setConnectionId(existingConnectionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  useEffect(() => {
    if (sendInviteFlag === ReducerFlag.SUCCESS) {
      FlashMessage("Connection request sent successfully!");
      // Update connection status to pending
      const pendingStatus = connectionRequestStatus.find(
        (row) => row.status_text === "pending"
      )?.status_id || 1;
      setConnectionStatus(pendingStatus);
      setConnectionId(_.get(sendInviteData, "id", null));
      dispatch(resetSendInvitationFlag());
      invitationModalHandleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendInviteFlag]);

  return (
    <div>
      <div className="main-container">
        <Modal
          visible={props.status && !invitationModalStatus}
          onClose={candidateModalHandleClose}
          className="candidateModalContainer new-profile-quick-view"
          title={"Quick View: " + _.get(profile, "first_name", "") + "'s Profile"}
        >
          <Spinner visible={profileLoading}>

            <div className="new-profile-quick-view-body">
              <div className="left-content">
                <div className="profile-info-card quick-view-card">
                  <div className="card-banner"></div>
                  <div className="profile-image">
                    <Avatar src={_.get(profile, "profile_image", '')} alt="Profile Image" />
                    {!_.isEmpty(_.get(profile, "profile.profile_video", "")) && (
                      <span
                        className="play-button"
                        onClick={() => videoShowModal(_.get(profile, "profile.profile_video", ""))}
                      >
                        <PlayArrowIcon />
                      </span>
                    )}
                  </div>

                  <div className="profile-info-body">
                    <div className="profile-info">
                      <div className="profile-name">
                        {_.get(profile, "first_name", "") + " " + _.get(profile, "last_name", "") + getGenderLabelSuffix(profile)}
                      </div>
                      <div className="profile-title">
                        {jobTitleCustom ? jobTitleCustom : jobTitle} - {workStatus}
                      </div>
                    </div>
                    <div className="profile-actions">
                      {renderConnectButton()}
                      {showMessageButton && (
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleQuickViewMessageClick}
                          disabled={!sessionUserEmail || isOwnProfile}
                        >
                          Message
                        </Button>
                      )}
                    </div>
                    <div className="quick-view-profile-menu-row">
                      <IconButton
                        className="quick-view-profile-more-btn"
                        aria-label="Profile options"
                        onClick={(e) =>
                          setQuickViewProfileMenuAnchor(e.currentTarget)
                        }
                      >
                        <img src={DotsIcon} alt="DotsIcon" />
                      </IconButton>
                      <Menu
                        id="quick-view-profile-options-menu"
                        anchorEl={quickViewProfileMenuAnchor}
                        keepMounted
                        open={Boolean(quickViewProfileMenuAnchor)}
                        onClose={() => setQuickViewProfileMenuAnchor(null)}
                        classes={{ paper: "border-radius-md" }}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <MenuItem onClick={openCandidatePublicProfileInNewTab}>
                          View full profile
                        </MenuItem>
                        <MenuItem onClick={openShareProfileModal}>
                          Share profile
                        </MenuItem>
                      </Menu>
                    </div>
                  </div>

                </div>

                <Menu
                  id="quick-view-message-menu"
                  anchorEl={msgMenuAnchor}
                  keepMounted
                  open={Boolean(msgMenuAnchor)}
                  onClose={handleQuickViewMessageMenuClose}
                  classes={{ paper: "border-radius-md" }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                >
                  {(sessionReducer?.companies || []).map(
                    (row: { slug?: string; company_name?: string }, key: number) => (
                      <MenuItem
                        key={key}
                        onClick={() =>
                          handleQuickViewRedirectToMessages(true, row)
                        }
                      >
                        as {row?.company_name}
                      </MenuItem>
                    )
                  )}
                  {connectAccepted && (
                    <MenuItem
                      onClick={() => handleQuickViewRedirectToMessages(false)}
                    >
                      as{" "}
                      {`${sessionReducer?.first_name || ""} ${sessionReducer?.last_name || ""}`.trim()}
                    </MenuItem>
                  )}
                </Menu>

                {
                  !_.isEmpty(_.get(profile, "skills", [])) && (
                    <div className="skills-card">
                      <div className="card-title">Skills</div>
                      <div className="quick-view-card">
                        <div className="card-content">
                          <div className="skills-list">
                            {_.get(profile, "skills", []).map((skill, index) => (
                              <div className="skill-item" key={index}>{skill?.title}</div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                }

                {!_.isEmpty(quickViewResumes) && (
                  <div className="resume-card skills-card">
                    <div className="card-title">Resume</div>
                    <div className="quick-view-card">
                      <div className="card-content">
                        {quickViewResumes?.map((resume: any) => (
                          <div
                            className="resume-content-wrapper"
                            key={resume.id}
                          >
                            <div className="resume-icon">
                              <img src={resumeIcon} alt="Resume Icon" />
                            </div>
                            <div className="resume-content">
                              <h6>{resume.original_file || "Resume"}</h6>
                              <p>
                                {resume.created_at
                                  ? `Uploaded on ${formatDate(resume.created_at)}`
                                  : ""}
                              </p>
                            </div>
                            <div className="resume-actions-right">
                              <IconButton
                                className="resume-three-dots"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPublicResumeMenuAnchor(e.currentTarget);
                                  setPublicMenuResume(resume);
                                }}
                                aria-label="Resume options"
                              >
                                <img src={DotsIcon} alt="DotsIcon" />

                              </IconButton>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Menu
                        id="quick-view-public-resume-menu"
                        anchorEl={publicResumeMenuAnchor}
                        keepMounted
                        open={Boolean(publicResumeMenuAnchor)}
                        onClose={() => {
                          setPublicResumeMenuAnchor(null);
                          setPublicMenuResume(null);
                        }}
                        classes={{ paper: "border-radius-md" }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                      >
                        <MenuItem
                          disabled={loadingPublicResumePreview}
                          onClick={() =>
                            publicMenuResume && handleShowResume(publicMenuResume)
                          }
                        >
                          View
                        </MenuItem>
                        <MenuItem
                          onClick={() =>
                            publicMenuResume &&
                            handleResumeDownload(publicMenuResume)
                          }
                        >
                          Download
                        </MenuItem>
                      </Menu>
                    </div>
                  </div>
                )}

                {!_.isEmpty(_.get(profile, "services", [])) && (
                  <div className="skills-card">
                    <div className="card-title">Services</div>
                    <div className="quick-view-card">
                      <div className="card-content">
                        {_.get(profile, "services", []).map(
                          (service: Record<string, unknown>, index: number) =>
                            renderQuickViewServiceEntry(service, index)
                        )}
                      </div>
                    </div>
                  </div>
                )}

              </div>
              <div className="right-content">
                {
                  renderPersonalValues(_.get(profile, "profile.personal_values")) && (
                    <div className="personal-values-card skills-card">
                      <div className="card-title">Personal Values</div>
                      <div className="quick-view-card">
                        <div className="card-content">
                          <p className="card-content-text">
                            {renderPersonalValues(_.get(profile, "profile.personal_values"))}
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                }

                <div className="education-card skills-card">
                  <div className="card-title">Education</div>
                  <div className="quick-view-card">
                    <div className="card-content multiple-items-card">
                      {
                        !_.isEmpty(_.get(profile, "education", [])) ? (
                          _.get(profile, "education", []).map((item, index) => (
                            <div className="item-row" key={index}>
                              <div className="item-icon">
                                <img src={educationIcon} alt="Education Icon" />
                              </div>
                              <div className="item-content">
                                <h6>{item?.degree_title} </h6>
                                <p>{item?.institute_name}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div>
                            <p>No education found for this candidate</p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                <div className="experience-card skills-card">
                  <div className="card-title">Work Experience</div>
                  <div className="quick-view-card">
                    <div className="card-content multiple-items-card">
                      {
                        !_.isEmpty(_.get(profile, "experiences", [])) ? (
                          _.get(profile, "experiences", []).map((item, index) => (
                            <div className="item-row" key={index}>
                              <div className="item-icon">
                                <img src={experienceIcon} alt="Experience Icon" />
                              </div>
                              <div className="item-content">
                                <h6>{formatQuickViewExperienceHeadline(item)}</h6>
                                <p>{item?.company_name} {item?.job_started ? `. ${formatDate(item?.job_started)}` : ""} {item?.is_present === 1 ? "- Present" : item?.job_ended ? `- ${formatDate(item?.job_ended)}` : " - "}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div>
                            <p>No experience found for this candidate</p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                <div className="projects-card skills-card">
                  <div className="card-title">Projects</div>
                  <div className="quick-view-card">
                    <div className="card-content multiple-items-card">
                      {
                        !_.isEmpty(_.get(profile, "projects", [])) ? (
                          _.get(profile, "projects", []).map((item, index) => (
                            <div className="item-row" key={index}>
                              <div className="item-icon">
                                <img src={projectIcon} alt="Project Icon" />
                              </div>
                              <div className="item-content">
                                <h6>{item?.title}</h6>
                                <p>{item?.company_name ? item?.company_name + " | " : ""} {item?.link ? item?.link : ""}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div>
                            <p>No projects found for this candidate</p>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
                {(!_.isEmpty(quickViewAccomplishments) ||
                  !_.isEmpty(quickViewCertificates)) && (
                    <div className="item-row">
                      <div className="accomplishments-card skills-card">
                        <div className="card-title">Accomplishments</div>
                        <div className="quick-view-card">
                          <div className="quick-view-card-content">
                            {!_.isEmpty(quickViewAccomplishments) && (
                              <div className="quick-view-accomplishment-group">
                                <div className="quick-view-accomplishment-group-title">
                                  Award
                                </div>
                                <div className="card-content multiple-items-card">
                                  {quickViewAccomplishments.map((item: any, index: number) => (
                                    <div className="item-row" key={item?.id ?? `award-${index}`}>
                                      <div className="item-icon">
                                        <img
                                          src={accomplishmentIcon}
                                          alt="Accomplishment Icon"
                                        />
                                      </div>
                                      <div className="item-content">
                                        <h6>{item?.award_name}</h6>
                                        <p>{item?.organization_name}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {!_.isEmpty(quickViewCertificates) && (
                              <div className="quick-view-accomplishment-group">
                                <div className="quick-view-accomplishment-group-title">
                                  Certificate
                                </div>
                                <div className="card-content multiple-items-card">


                                  {quickViewCertificates.map((item: any, index: number) => (
                                    <div
                                      className="item-row"
                                      key={item?.id ?? `cert-${index}`}
                                    >
                                      <div className="item-icon">
                                        <img
                                          src={accomplishmentIcon}
                                          alt="Certificate Icon"
                                        />
                                      </div>
                                      <div className="item-content">
                                        <h6>{item?.cert_name}</h6>
                                        <p>{formatQuickViewCertificateDetail(item)}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </Spinner>
        </Modal>
      </div>
      <Modal
        visible={shareProfileModalOpen}
        onClose={() => setShareProfileModalOpen(false)}
        title="Share profile"
        size="large"
        className="candidate-share-profile-url-modal"

      >
        <div className="candidate-share-profile-url-modal-body">
          <p className="candidate-share-profile-url-label">
            Share your profile URL on other social media
          </p>
          <div className="candidate-share-profile-url-copy-row">
            <div className="candidate-share-profile-url-field">
              {candidatePublicProfileUrl || "—"}

            </div>
            <div className="icon" onClick={copyCandidatePublicProfileUrl}>
              <LinkSVG fill="#ffffff" />
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        visible={resumeViewModal}
        onClose={() => setResumeViewModal(false)}
        title="View Resume"
        size="x-large"
        className="resume-upload-modal"
      >
        <div className="flex-column align-items-center mb-20">
          <DocViewer
            config={{
              header: {
                disableHeader: true,
                disableFileName: true,
                retainURLParams: true,
              },
            }}
            style={{ width: "100%", height: "auto" }}
            documents={[{ uri: resumePreviewPath }]}
            pluginRenderers={DocViewerRenderers}
          />
        </div>
      </Modal>
      <VideoModal
        status={videoModalStatus}
        candidateVideoUrl={candidateVideoUrl}
        handleOnClose={videoModalHandleClose}
      />
      <InvitationModal
        open={invitationModalStatus}
        payload={profile}
        onInviteSent={handleOnInviteSent}
        handleOnClose={invitationModalHandleClose}
      />
      <ConfirmDialog
        visible={cancelSentRequestShow}
        title={`Withdraw sent request`}
        onCancel={() => setCancelSentRequestShow(false)}
        bodyText={`Request to withdraw will not allow to reconnect with this person for up to ${RESEND_CONNECTION_REQUEST_TIME_LIMIT} hours.`}
        onConfirm={handleCancelSentRequest}
        confirmText="Okay"
        cancelText="Cancel"
      />
    </div>
  );
};

export default CandidatesModal;
